import React from 'react';
import {
    Document,
    Page,
    Text,
    View,
    StyleSheet,
    Image
} from '@react-pdf/renderer';
import { LancamentoFinanceiro } from '../../../../types/financeiro';

// Estilos do documento - Paisagem
const styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        backgroundColor: '#ffffff',
        padding: 20,
        fontFamily: 'Helvetica',
        fontSize: 7,
        color: '#333333',
    },
    headerBox: {
        flexDirection: 'row',
        marginBottom: 10,
        borderBottom: '0.5px solid #e0e0e0',
        paddingBottom: 8,
        alignItems: 'center',
        justifyContent: 'space-between'
    },
    title: {
        fontSize: 14,
        fontFamily: 'Helvetica-Bold',
        color: '#333333',
    },
    subtitle: {
        fontSize: 9,
        color: '#666666',
        marginTop: 2,
    },
    logo: {
        width: 100,
        height: 40,
        objectFit: 'contain',
    },
    tableContainer: {
        marginTop: 5,
        borderWidth: 0.5,
        borderColor: '#e0e0e0',
    },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#f5f5f5',
        borderBottomWidth: 0.5,
        borderBottomColor: '#e0e0e0',
        alignItems: 'center',
        height: 15,
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 0.5,
        borderBottomColor: '#e0e0e0',
        alignItems: 'center',
        minHeight: 12,
        paddingVertical: 2,
    },
    // Colunas (Total 100%)
    colData: { width: '8%', paddingLeft: 4 },
    colCliente: { width: '15%', paddingLeft: 4 },
    colForma: { width: '10%', paddingLeft: 4 },
    colLancamento: { width: '18%', paddingLeft: 4 },
    colAnotacoes: { width: '15%', paddingLeft: 4 },
    colCentro: { width: '10%', paddingLeft: 4 },
    colDebito: { width: '8%', textAlign: 'right', paddingRight: 4 },
    colCredito: { width: '8%', textAlign: 'right', paddingRight: 4 },
    colSaldo: { width: '8%', textAlign: 'right', paddingRight: 4 },

    headerText: {
        fontFamily: 'Helvetica-Bold',
        fontSize: 6,
    },
    cellText: {
        fontSize: 6,
    },
    debitText: {
        color: '#d32f2f', // Vermelho
    },
    creditText: {
        color: '#333333', // Preto padrão
    },
    saldoPositive: {
        color: '#1976d2', // Azul
        fontFamily: 'Helvetica-Bold',
    },
    saldoNegative: {
        color: '#d32f2f', // Vermelho
        fontFamily: 'Helvetica-Bold',
    },
    // Estilo para itens estornados
    strikethrough: {
        textDecoration: 'line-through',
        color: '#999999',
    },
    footer: {
        position: 'absolute',
        bottom: 10,
        left: 20,
        right: 20,
        textAlign: 'center',
        fontSize: 6,
        color: '#999999',
        borderTopWidth: 0.5,
        borderTopColor: '#e0e0e0',
        paddingTop: 4,
    },
});

interface RelatorioFinanceiroDocumentProps {
    lancamentos: LancamentoFinanceiro[];
    periodo: {
        inicio: string;
        fim: string;
    };
    logoUrl?: string;
}

const formatCurrency = (value: number) => {
    return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDate = (dateString: string) => {
    // Evitar conversão de fuso horário, usar partes da string
    if (!dateString) return '-';
    const parts = dateString.split('-'); // YYYY-MM-DD
    if (parts.length !== 3) return dateString;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

export const RelatorioFinanceiroDocument: React.FC<RelatorioFinanceiroDocumentProps> = ({ 
    lancamentos, 
    periodo,
    logoUrl = '/assets/img/A2R_logo.png' 
}) => {
    
    // Cálculos de Totais (Excluir Estornados)
    const totalEntradas = lancamentos
        .filter(l => l.tipo === 'RECEITA' && l.situacao === 'CONFIRMADO')
        .reduce((acc, curr) => acc + (Number(curr.valor) || 0), 0);
        
    const totalSaidas = lancamentos
        .filter(l => l.tipo === 'DESPESA' && l.situacao === 'CONFIRMADO')
        .reduce((acc, curr) => acc + (Number(curr.valor) || 0), 0);
        
    const resultadoPeriodo = totalEntradas - totalSaidas;

    return (
        <Document>
            <Page size="A4" orientation="landscape" style={styles.page}>
                
                {/* Cabeçalho */}
                <View style={styles.headerBox}>
                    <View>
                        <Text style={styles.title}>Relatório Financeiro</Text>
                        <Text style={styles.subtitle}>Período: {periodo.inicio} à {periodo.fim}</Text>
                        <Text style={styles.subtitle}>Gerado em: {new Date().toLocaleDateString('pt-BR')} às {new Date().toLocaleTimeString('pt-BR')}</Text>
                    </View>
                    <Image src={logoUrl} style={styles.logo} />
                </View>

                {/* Resumo Rápido */}
                <View style={{ flexDirection: 'row', marginBottom: 10, gap: 20 }}>
                     <View>
                        <Text style={{ fontSize: 7, color: '#666' }}>Total Receitas (Confirmadas)</Text>
                        <Text style={{ fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#2e7d32' }}>
                            R$ {formatCurrency(totalEntradas)}
                        </Text>
                     </View>
                     <View>
                        <Text style={{ fontSize: 7, color: '#666' }}>Total Despesas (Confirmadas)</Text>
                        <Text style={{ fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#d32f2f' }}>
                            R$ {formatCurrency(totalSaidas)}
                        </Text>
                     </View>
                     <View>
                        <Text style={{ fontSize: 7, color: '#666' }}>Resultado do Período</Text>
                        <Text style={{ fontSize: 8, fontFamily: 'Helvetica-Bold', color: resultadoPeriodo >= 0 ? '#1976d2' : '#d32f2f' }}>
                            R$ {formatCurrency(resultadoPeriodo)}
                        </Text>
                     </View>
                </View>

                {/* Tabela */}
                <View style={styles.tableContainer}>
                    {/* Cabeçalho da Tabela */}
                    <View style={styles.tableHeader}>
                        <View style={styles.colData}><Text style={styles.headerText}>DATA</Text></View>
                        <View style={styles.colCliente}><Text style={styles.headerText}>CLIENTE/FORNECEDOR</Text></View>
                        <View style={styles.colForma}><Text style={styles.headerText}>FORMA</Text></View>
                        <View style={styles.colLancamento}><Text style={styles.headerText}>LANÇAMENTO</Text></View>
                        <View style={styles.colAnotacoes}><Text style={styles.headerText}>ANOTAÇÕES</Text></View>
                        <View style={styles.colCentro}><Text style={styles.headerText}>CENTRO CUSTO</Text></View>
                        <View style={styles.colDebito}><Text style={styles.headerText}>DÉBITO</Text></View>
                        <View style={styles.colCredito}><Text style={styles.headerText}>CRÉDITO</Text></View>
                        <View style={styles.colSaldo}><Text style={styles.headerText}>SALDO</Text></View>
                    </View>

                    {/* Linhas */}
                    {lancamentos.map((l, index) => {
                        const valor = Number(l.valor) || 0;
                        const saldo = Number(l.saldo_apos_transacao);
                        const isDespesa = l.tipo === 'DESPESA';
                        const isEstornado = l.situacao !== 'CONFIRMADO';
                        
                        // Estilo base da linha
                        const baseStyle = [
                            styles.cellText,
                            isEstornado ? styles.strikethrough : {}
                        ];

                        // Descrição com tag se estornado
                        const descricaoFinal = isEstornado 
                            ? `[ESTORNADO] ${l.descricao}`
                            : l.descricao;
                        
                        return (
                            <View key={index} style={[
                                styles.tableRow, 
                                { backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9f9f9' }
                            ]}>
                                <View style={styles.colData}>
                                    <Text style={baseStyle}>{formatDate(l.data_lancamento)}</Text>
                                </View>
                                <View style={styles.colCliente}>
                                    <Text style={baseStyle}>{l.pessoa_nome || '-'}</Text>
                                </View>
                                <View style={styles.colForma}>
                                    <Text style={baseStyle}>{l.forma_pagamento_nome || '-'}</Text>
                                </View>
                                <View style={styles.colLancamento}>
                                    <Text style={baseStyle}>{descricaoFinal}</Text>
                                </View>
                                <View style={styles.colAnotacoes}>
                                    <Text style={baseStyle}>{l.observacoes || '-'}</Text>
                                </View>
                                <View style={styles.colCentro}>
                                    <Text style={baseStyle}>{l.categoria_nome || '-'}</Text>
                                </View>
                                <View style={styles.colDebito}>
                                    <Text style={[
                                        styles.cellText,
                                        isEstornado ? styles.strikethrough : styles.debitText
                                    ]}>
                                        {isDespesa ? formatCurrency(valor) : ''}
                                    </Text>
                                </View>
                                <View style={styles.colCredito}>
                                    <Text style={[
                                        styles.cellText,
                                        isEstornado ? styles.strikethrough : styles.creditText
                                    ]}>
                                        {!isDespesa ? formatCurrency(valor) : ''}
                                    </Text>
                                </View>
                                <View style={styles.colSaldo}>
                                    {l.saldo_apos_transacao !== undefined && l.saldo_apos_transacao !== null ? (
                                        <Text style={[
                                            styles.cellText,
                                            isEstornado ? styles.strikethrough : (saldo < 0 ? styles.saldoNegative : styles.saldoPositive)
                                        ]}>
                                            {formatCurrency(saldo)}
                                        </Text>
                                    ) : (
                                        <Text style={baseStyle}>-</Text>
                                    )}
                                </View>
                            </View>
                        );

                    })}
                </View>

                {/* Rodapé da Página */}
                <Text style={styles.footer} render={({ pageNumber, totalPages }) => (
                    `Página ${pageNumber} de ${totalPages}`
                )} fixed />
            </Page>
        </Document>
    );
};